// Email + password sign-in. Sanctum cookie session — no token returned;
// the cookie is set by the server and credentials: 'include' on
// subsequent requests keeps the SPA authenticated.
//
// On success: refresh AuthContext (which re-fetches /me) and route
// based on the user's role.

import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Loader2, Mail, Lock, ShoppingBag, Users, Store, Shield, Smartphone } from 'lucide-react';
import { toast } from 'sonner';
import { ApiError, auth, type Me } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import { ViewOnPhoneButton } from '../../components/shared/ViewOnPhoneButton';

function defaultLandingFor(user: Me): string {
  switch (user.role) {
    case 'admin':
    case 'super_admin': return '/admin';
    case 'merchant':    return '/merchant';
    case 'recipient':   return '/recipient';
    case 'customer':
    default:            return '/customer';
  }
}

export function Login() {
  const { t } = useTranslation();
  const { refresh } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const returnTo = params.get('returnTo');

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const submitWith = async (e: string, p: string) => {
    setSubmitting(true);
    try {
      const user = await auth.login(e.trim(), p);
      await refresh();
      // Greet by name, not raw email — consistent with the "Bonjou, <name>" hero.
      const fullName = (user as { customer_profile?: { full_name?: string } }).customer_profile?.full_name;
      const greetName = (fullName || user.email || '').split(/[ @]/)[0];
      toast.success(`Bonjou, ${greetName}`);
      navigate(returnTo || defaultLandingFor(user), { replace: true });
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : 'Sign-in failed';
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const onSubmit = async (ev: React.FormEvent) => {
    ev.preventDefault();
    await submitWith(email, password);
  };

  const loginAs = (e: string) => submitWith(e, 'demo1234');

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      <Header />
      <main className="flex-1 grid place-items-center px-6 py-12">
        <div className="w-full max-w-md bg-white rounded-2xl border border-slate-200 shadow-sm p-7">
          <h1 className="text-[22px] font-semibold tracking-tight text-slate-900">Sign in to VoyeLakay</h1>
          <p className="mt-1 text-[13px] text-slate-500">Manage family support, requests, and payouts.</p>

          <form onSubmit={onSubmit} className="mt-6 space-y-4">
            <Field icon={Mail} label="Email" type="email" value={email} onChange={setEmail} autoComplete="email" required />
            <Field icon={Lock} label="Password" type="password" value={password} onChange={setPassword} autoComplete="current-password" required />
            <div className="text-right -mt-2">
              <Link to="/forgot-password" className="text-[12px] font-semibold text-brand-600 hover:underline">Forgot password?</Link>
            </div>

            <button
              type="submit"
              disabled={submitting}
              className="w-full inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-lg bg-brand-600 text-white text-[14px] font-semibold hover:bg-brand-700 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              {submitting ? 'Signing in…' : 'Sign in'}
            </button>
          </form>

          <p className="mt-5 text-[12.5px] text-slate-500 text-center">
            New to VoyeLakay?{' '}
            <Link to={`/register?role=customer${returnTo ? `&returnTo=${encodeURIComponent(returnTo)}` : ''}`} className="text-brand-600 font-semibold hover:underline">
              Create an account
            </Link>
          </p>
        </div>

        {/* Demo accounts — one-click sign-in for testers. Build-time gated:
            this panel names four addresses INCLUDING the platform admin with
            the shared password beside them, which on a public login page is a
            map of how to get in. The production demo accounts have been purged
            or suspended, so it would advertise logins that cannot work. */}
        {import.meta.env.VITE_DEMO_LOGINS === 'true' && (
        <div className="mt-6 w-full max-w-md bg-white rounded-2xl border border-dashed border-slate-300 p-5">
          <div className="flex items-center justify-between">
            <p className="text-[11px] uppercase tracking-wider font-semibold text-slate-500">Try a demo account</p>
            <span className="text-[10px] text-slate-400">password: demo1234</span>
          </div>
          <div className="mt-3 grid gap-2">
            <DemoButton icon={Users}      label="Diaspora customer" sub="demo-customer@lakaycart.com"           disabled={submitting} onClick={() => loginAs('demo-customer@lakaycart.com')} />
            <DemoButton icon={Smartphone} label="HT recipient (Maria)" sub="demo-recipient@lakaycart.com · Léogâne" disabled={submitting} onClick={() => loginAs('demo-recipient@lakaycart.com')} />
            <DemoButton icon={Store}      label="Approved merchant"  sub="demo-merchant@lakaycart.com · Léogâne"  disabled={submitting} onClick={() => loginAs('demo-merchant@lakaycart.com')} />
            <DemoButton icon={Shield}     label="Platform admin"     sub="demo-admin@lakaycart.com"                disabled={submitting} onClick={() => loginAs('demo-admin@lakaycart.com')} />
          </div>
        </div>
        )}

        <div className="mt-5 flex justify-center">
          <ViewOnPhoneButton />
        </div>

        <p className="mt-6 text-[11px] text-slate-400">
          By signing in you agree to our <Link to="/terms" className="hover:text-slate-700">Terms</Link> and <Link to="/privacy" className="hover:text-slate-700">Privacy notice</Link>.
        </p>
        <span className="sr-only">{t('nav.signIn')}</span>
      </main>
    </div>
  );
}

function Header() {
  return (
    <header className="bg-white border-b border-slate-100">
      <div className="mx-auto max-w-7xl px-6 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2.5">
          <span className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-gradient-to-br from-brand-500 to-brand-700 text-white">
            <ShoppingBag className="w-4 h-4" />
          </span>
          <span className="font-semibold tracking-tight">VoyeLakay</span>
        </Link>
        <Link to="/" className="text-[13px] text-slate-500 hover:text-slate-900">← Back</Link>
      </div>
    </header>
  );
}

function DemoButton({
  icon: Icon, label, sub, onClick, disabled,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  sub: string;
  onClick: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 hover:border-slate-300 transition-colors text-left disabled:opacity-50 disabled:cursor-not-allowed"
    >
      <span className="inline-flex w-8 h-8 items-center justify-center rounded-lg bg-brand-50 text-brand-600 shrink-0">
        <Icon className="w-4 h-4" />
      </span>
      <span className="flex-1 min-w-0">
        <span className="block text-[13px] font-semibold text-slate-900">{label}</span>
        <span className="block text-[11px] text-slate-500 truncate font-mono">{sub}</span>
      </span>
      <span className="text-[11px] text-brand-600 font-semibold shrink-0">Sign in →</span>
    </button>
  );
}

function Field({
  icon: Icon, label, type, value, onChange, autoComplete, required,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  type: string;
  value: string;
  onChange: (v: string) => void;
  autoComplete?: string;
  required?: boolean;
}) {
  return (
    <label className="block">
      <span className="text-[11px] uppercase tracking-wider font-semibold text-slate-500">{label}</span>
      <div className="relative mt-1 flex items-center bg-white rounded-lg border border-slate-200 focus-within:ring-2 focus-within:ring-brand-500/30 focus-within:border-brand-400">
        <Icon className="absolute left-3 w-4 h-4 text-slate-400" />
        <input
          type={type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          autoComplete={autoComplete}
          required={required}
          className="w-full pl-9 pr-3 py-2.5 text-[14px] bg-transparent rounded-lg focus:outline-none"
        />
      </div>
    </label>
  );
}
